import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  getDisplayTimeZone,
  resolveTimeZone,
  zoneLabel,
} from '@/lib/displayTimeZone';
import { formatZonedDate } from '@/lib/date-utils';
import { useAbsoluteTimeFormat } from '@/hooks/useAbsoluteTimeFormat';
import type { DisplayTimeZone, RelativePreset, TimeRange } from './types';
import { RELATIVE_PRESETS, timeZoneOptions } from './constants';
import {
  formatDateTimeInZone,
  formatUtcLabel,
  getAvailablePresets,
  getRangeByKey,
  parseDateTimeInZone,
} from './utils';
import { useCallback, useMemo, useRef, useState, type FC } from 'react';

/* =======================
 * Trigger
 * ======================= */

function formatDateDisplay(
  date: Date,
  tz: DisplayTimeZone | undefined
): string {
  return formatZonedDate(date, resolveTimeZone(tz ?? getDisplayTimeZone()));
}

/** The zone as the selector's options label it, offset included. */
function getTimezoneLabel(tz: DisplayTimeZone | undefined): string | null {
  return tz ? zoneLabel(resolveTimeZone(tz), new Date()) : null;
}

function createTriggerNode(
  pending: TimeRange | undefined,
  timezone: DisplayTimeZone | undefined
) {
  if (!pending) {
    return (
      <span className="text-sm text-muted-foreground">Select time range</span>
    );
  }

  const tzLabel = getTimezoneLabel(timezone);

  return pending.mode === 'relative' ? (
    <>
      <PopoverTrigger asChild>
        <span className="cursor-pointer text-base font-bold underline text-primary">
          {RELATIVE_PRESETS.find(p => p.key === pending.preset)?.label}
          {tzLabel && (
            <span className="inline text-[10px] text-muted-foreground">
              {` - ${tzLabel}`}
            </span>
          )}
        </span>
      </PopoverTrigger>
    </>
  ) : (
    <>
      <PopoverTrigger asChild>
        <span className="cursor-pointer underline text-sm text-primary">
          {formatDateDisplay(pending.from, timezone)} →{' '}
          {formatDateDisplay(pending.to, timezone)}
          {tzLabel && (
            <span className="inline text-[10px] text-muted-foreground">
              {` - ${tzLabel}`}
            </span>
          )}
        </span>
      </PopoverTrigger>
    </>
  );
}

export interface TimeFilter {
  range: TimeRange;
  timezone: DisplayTimeZone;
}

interface TimeRangeSelectorProps {
  onChange: (timeFilter: TimeFilter) => void;
  timeFilter: TimeFilter;
  className?: string;
  /** Minimum allowed date for time selection (e.g., organization createdAt) */
  minDate?: Date;
  /**
   * When true, the popover shows only the relative "Last …" presets — the
   * absolute From/To inputs, timezone picker, and Cancel/Apply footer are
   * hidden, and picking a preset applies immediately. Used by widgets that
   * only support relative ranges (e.g. Coverage Over Time, KIR-3823).
   */
  presetsOnly?: boolean;
}

export const TimeRangeSelector: FC<TimeRangeSelectorProps> = ({
  onChange,
  timeFilter,
  className,
  minDate,
  presetsOnly = false,
}) => {
  // The trigger's dates follow the date-format setting.
  useAbsoluteTimeFormat();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const didApplyRef = useRef(false);

  const [pending, setPending] = useState<TimeFilter | null>(() => {
    if (timeFilter) return timeFilter;
    const preset = getRangeByKey('7d');
    if (!preset) return null;
    const timezone = getDisplayTimeZone();
    const { from, to } = preset.getRange(timezone);
    return {
      range: { mode: 'relative', preset: '7d', from, to },
      timezone,
    };
  });

  // Controlled input state - allows free typing without immediate parsing
  const [fromStr, setFromStr] = useState(() =>
    formatDateTimeInZone(pending?.range.from, pending?.timezone)
  );
  const [toStr, setToStr] = useState(() =>
    formatDateTimeInZone(pending?.range.to, pending?.timezone)
  );

  // Sync input strings with pending state (used when presets/timezone change)
  const syncInputStrings = useCallback((filter: TimeFilter) => {
    setFromStr(formatDateTimeInZone(filter.range.from, filter.timezone));
    setToStr(formatDateTimeInZone(filter.range.to, filter.timezone));
  }, []);

  // Show a filter the parent replaced while the popover is closed (a widget
  // following a Settings time-zone change); an open popover keeps the user's
  // edits until Apply or Cancel.
  const [shownFilter, setShownFilter] = useState(timeFilter);
  if (timeFilter !== shownFilter) {
    setShownFilter(timeFilter);
    if (!open && timeFilter) {
      setPending(timeFilter);
      syncInputStrings(timeFilter);
    }
  }

  // Filter presets based on minDate - only show presets where from >= minDate
  const availablePresets = useMemo(
    () => getAvailablePresets(minDate, pending?.timezone),
    [minDate, pending?.timezone]
  );

  // Zone labels carry each zone's offset now: rebuilt on every open.
  const zoneOptions = useMemo(
    () => (open && !presetsOnly ? timeZoneOptions() : []),
    [open, presetsOnly]
  );

  /* -------- handlers -------- */

  const applyPreset = (key: RelativePreset) => {
    const preset = getRangeByKey(key);
    if (!preset) return;
    const tz = pending?.timezone ?? 'browser';
    const { from, to } = preset.getRange(tz);
    const nextFilter: TimeFilter = {
      range: { mode: 'relative', preset: key, from, to },
      timezone: tz,
    };
    setPending(nextFilter);
    syncInputStrings(nextFilter);
    setError(null);

    // In presets-only mode there is no Apply button, so committing the preset
    // must both propagate the change and close the popover immediately.
    if (presetsOnly) {
      didApplyRef.current = true;
      onChange(nextFilter);
      setOpen(false);
    }
  };

  // Validate and update error state on every change. Also updates the input
  // string state so the user can type freely without the input reverting.
  const changeAbsolute = (which: 'from' | 'to', value: string): void => {
    // When the browser's native datetime-local picker auto-fills the time
    // (e.g. after clearing and re-picking a date), it uses the current time.
    // For the "from" field this is almost never desired — snap to midnight.
    if (which === 'from' && value) {
      const prevDatePart = fromStr.split('T')[0];
      const [newDatePart, newTimePart] = value.split('T');
      if (newDatePart && newTimePart && newDatePart !== prevDatePart) {
        const now = new Date();
        const nowHH = now.getHours().toString().padStart(2, '0');
        const nowMM = now.getMinutes().toString().padStart(2, '0');
        const nowTime = `${nowHH}:${nowMM}`;
        if (newTimePart === nowTime) {
          value = `${newDatePart}T00:00`;
        }
      }
    }

    if (which === 'from') setFromStr(value);
    else setToStr(value);

    if (!pending?.timezone) return;

    const parsed = parseDateTimeInZone(value, pending.timezone);
    if (!parsed) {
      setError('Invalid date — please enter a complete date and time.');
      return;
    }

    // Use the current string values for the OTHER field so cross-field
    // validation reflects what the user has typed, not just the last committed
    // pending state (which only updates on blur via commitAbsolute).
    const otherStr = which === 'from' ? toStr : fromStr;
    const otherParsed =
      parseDateTimeInZone(otherStr, pending.timezone) ??
      (which === 'from' ? pending.range.to : pending.range.from);

    const nextFrom = which === 'from' ? parsed : otherParsed;
    const nextTo = which === 'to' ? parsed : otherParsed;

    if (minDate && nextFrom < minDate) {
      setError('Start date cannot be before organization creation.');
    } else if (nextFrom > nextTo) {
      setError('End date must be after start date.');
    } else {
      setError(null);
    }
  };

  // Commit the parsed date to pending state on blur so UTC labels stay in sync.
  // Errors are already set/cleared by changeAbsolute on every keystroke.
  const commitAbsolute = (which: 'from' | 'to'): void => {
    if (!pending?.timezone) return;

    const value = which === 'from' ? fromStr : toStr;
    const parsed = parseDateTimeInZone(value, pending.timezone);
    if (!parsed) return; // error already shown by changeAbsolute

    // Don't switch relative → absolute if the user didn't actually change the value
    const expectedStr = formatDateTimeInZone(
      which === 'from' ? pending.range.from : pending.range.to,
      pending.timezone
    );
    if (pending.range.mode === 'relative' && value === expectedStr) return;

    const nextFrom = which === 'from' ? parsed : pending.range.from;
    const nextTo = which === 'to' ? parsed : pending.range.to;

    setPending({
      range: { mode: 'absolute', from: nextFrom, to: nextTo },
      timezone: pending.timezone,
    });
  };

  const cancel = () => {
    // Discard pending edits and revert to the last applied filter
    if (timeFilter) {
      setPending(timeFilter);
      syncInputStrings(timeFilter);
    }
    setError(null);
    setOpen(false);
  };

  const apply = () => {
    if (!pending?.timezone) return;

    // Compute expected strings first so we can detect user edits
    const expectedFromStr = formatDateTimeInZone(
      pending.range.from,
      pending.timezone
    );
    const expectedToStr = formatDateTimeInZone(
      pending.range.to,
      pending.timezone
    );

    // Parse current input values
    const parsedFrom = parseDateTimeInZone(fromStr, pending.timezone);
    const parsedTo = parseDateTimeInZone(toStr, pending.timezone);

    // If parse failed on a modified input, reject explicitly rather than
    // silently falling back to pending timestamps (which would convert a
    // relative preset into an absolute range with unchanged timestamps).
    if (!parsedFrom && fromStr !== expectedFromStr) {
      setError('Invalid start date format.');
      return;
    }
    if (!parsedTo && toStr !== expectedToStr) {
      setError('Invalid end date format.');
      return;
    }

    // Use parsed values or fall back to pending state (input was not changed)
    const finalFrom = parsedFrom ?? pending.range.from;
    const finalTo = parsedTo ?? pending.range.to;

    if (minDate && finalFrom < minDate) {
      setError('Start date cannot be before organization creation.');
      return;
    }

    if (finalFrom > finalTo) {
      setError('End date must be after start date.');
      return;
    }

    // Preserve relative mode if inputs weren't changed
    const inputsUnchanged =
      fromStr === expectedFromStr && toStr === expectedToStr;

    const finalFilter: TimeFilter = {
      range:
        pending.range.mode === 'relative' && inputsUnchanged
          ? pending.range
          : { mode: 'absolute', from: finalFrom, to: finalTo },
      timezone: pending.timezone,
    };

    setError(null);
    setPending(finalFilter);
    syncInputStrings(finalFilter);
    didApplyRef.current = true;
    onChange(finalFilter);
    setOpen(false);
  };

  /* -------- computed values -------- */

  const minDateStr = minDate
    ? formatDateTimeInZone(minDate, pending?.timezone)
    : undefined;

  const utcFromLabel = formatUtcLabel(pending?.range.from);
  const utcToLabel = formatUtcLabel(pending?.range.to);

  /* -------- trigger text -------- */

  const trigger = createTriggerNode(pending?.range, pending?.timezone);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      if (timeFilter) {
        // When opening, sync the selector with the last applied filter
        setPending(timeFilter);
        syncInputStrings(timeFilter);
      }
      setError(null);
      setOpen(true);
    } else {
      // When closing (including clicking outside), discard any pending edits
      // and revert to the last applied filter so the trigger reflects
      // the actually applied range.
      // Skip the reset if Apply caused this close — pending already holds the
      // correct new value and timeFilter prop may not have updated yet.
      if (!didApplyRef.current && timeFilter) {
        setPending(timeFilter);
        syncInputStrings(timeFilter);
      }
      didApplyRef.current = false;
      setError(null);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <span className={cn('inline-flex', className)}>{trigger}</span>

      {presetsOnly ? (
        <PopoverContent align="start" className="w-[180px] p-2">
          <div className="max-h-[300px] overflow-y-auto pr-1">
            <div className="flex flex-col gap-1">
              {availablePresets.map(p => {
                const active =
                  pending?.range.mode === 'relative' &&
                  pending.range.preset === p.key;

                return (
                  <Button
                    key={p.key}
                    size="sm"
                    variant={active ? 'secondary' : 'ghost'}
                    className="justify-start h-8 px-2"
                    onClick={() => applyPreset(p.key)}
                  >
                    {p.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </PopoverContent>
      ) : (
        <PopoverContent align="start" className="w-[520px] p-4">
          <div className="grid grid-cols-[1fr_160px] gap-4 h-[300px]">
            {/* LEFT */}
            <div className="flex flex-col justify-between">
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>From</Label>
                  <Input
                    type="datetime-local"
                    value={fromStr}
                    min={minDateStr}
                    onChange={e => changeAbsolute('from', e.target.value)}
                    onBlur={() => commitAbsolute('from')}
                  />
                  <div className="text-xs text-muted-foreground">
                    UTC:{' '}
                    <span className="font-mono text-foreground/80">
                      {utcFromLabel || '—'}
                    </span>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label>To</Label>
                  <Input
                    type="datetime-local"
                    value={toStr}
                    onChange={e => changeAbsolute('to', e.target.value)}
                    onBlur={() => commitAbsolute('to')}
                    min={fromStr}
                  />
                  <div className="text-xs text-muted-foreground">
                    UTC:{' '}
                    <span className="font-mono text-foreground/80">
                      {utcToLabel || '—'}
                    </span>
                  </div>
                </div>

                <div className="border-b pb-6 space-y-1">
                  <Label>Timezone</Label>

                  <Select
                    value={pending?.timezone}
                    onValueChange={v => {
                      if (!pending) return;
                      const nextFilter: TimeFilter = {
                        ...pending,
                        timezone: v as DisplayTimeZone,
                      };
                      setPending(nextFilter);
                      syncInputStrings(nextFilter);
                    }}
                  >
                    <SelectTrigger className="h-10 w-full">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent className="w-[--radix-select-trigger-width]">
                      {zoneOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {error && (
                  <div className="text-xs text-red-500" role="alert">
                    {error}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={cancel} className="w-2/5">
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={apply}
                  className="w-3/5"
                  disabled={!!error}
                >
                  Apply
                </Button>
              </div>
            </div>

            {/* RIGHT */}
            <div className="h-full overflow-y-auto pr-1">
              <div className="flex flex-col gap-1">
                {availablePresets.map(p => {
                  const active =
                    pending?.range.mode === 'relative' &&
                    pending.range.preset === p.key;

                  return (
                    <Button
                      key={p.key}
                      size="sm"
                      variant={active ? 'secondary' : 'ghost'}
                      className="justify-start h-8 px-2"
                      onClick={() => applyPreset(p.key)}
                    >
                      {p.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};
